import { useId, useState, type ReactNode } from 'react';
import { useSheetPlacement } from './adaptive-overlay';
import { KitButton } from './button';

export interface WeekDay {
  id: string;
  date: string;
  label: string;
  content: ReactNode;
}
export interface WeekGridProps {
  /** Caller supplies seven dates in the viewer locale's week order. */
  days: readonly WeekDay[];
  label: string;
  selectedDay?: string;
  onDayChange?(id: string): void;
}
export function WeekGrid({ days, label, selectedDay, onDayChange }: WeekGridProps) {
  const sheet = useSheetPlacement();
  const groupId = useId().replaceAll(':', '');
  const [chosen, setChosen] = useState(days[0]?.id);
  const current = selectedDay ?? chosen;
  const active = days.some((day) => day.id === current) ? current : days[0]?.id;
  const visible = sheet ? days.filter((day) => day.id === active) : days;
  return (
    <section className="pc-week" aria-label={label} data-view={sheet ? 'day' : 'week'}>
      {sheet && (
        <div className="pc-board-switch" role="tablist" aria-label={label}>
          {days.map((day, index) => (
            <KitButton
              key={day.id}
              variant="quiet"
              role="tab"
              id={`${groupId}-tab-${index}`}
              aria-controls={`${groupId}-panel-${index}`}
              tabIndex={day.id === active ? 0 : -1}
              aria-selected={day.id === active}
              onClick={() => {
                setChosen(day.id);
                onDayChange?.(day.id);
              }}
              onKeyDown={(event) => {
                if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
                const next =
                  event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? days.length - 1
                      : event.key === 'ArrowRight'
                        ? (index + 1) % days.length
                        : event.key === 'ArrowLeft'
                          ? (index + days.length - 1) % days.length
                          : null;
                if (next === null) return;
                event.preventDefault();
                document.getElementById(`${groupId}-tab-${next}`)?.focus();
                setChosen(days[next].id);
                onDayChange?.(days[next].id);
              }}
            >
              {day.label}
            </KitButton>
          ))}
        </div>
      )}
      {visible.map((day) => (
        <section
          key={day.id}
          id={`${groupId}-panel-${days.indexOf(day)}`}
          aria-label={day.label}
          aria-labelledby={sheet ? `${groupId}-tab-${days.indexOf(day)}` : undefined}
          role={sheet ? 'tabpanel' : undefined}
        >
          <h3>
            <time dateTime={day.date}>{day.label}</time>
          </h3>
          {day.content}
        </section>
      ))}
    </section>
  );
}
